/**
 * The token pair the auth endpoints return, normalised.
 *
 * `/v3/auth/login` and `/v3/auth/refresh` answer
 * `{ success, message, accessToken, refreshToken, user? }` — no `data`
 * envelope. Both login and the refresh-on-401 path read tokens through here,
 * so a backend that later renames the field (or wraps it) is a one-line change.
 */
import { z } from 'zod/mini';

const RawTokens = z.pipe(
  z.object({
    accessToken: z.string().check(z.minLength(1)),
    refreshToken: z.string().check(z.minLength(1)),
  }),
  z.transform((raw) => ({ accessToken: raw.accessToken, refreshToken: raw.refreshToken })),
);

export type AuthTokens = z.output<typeof RawTokens>;

/** Throws when the response carries no usable token pair. */
export const parseAuthTokens = (raw: unknown): AuthTokens => RawTokens.parse(raw);

/** `null` instead of throwing — for the refresh path, where a bad response just ends the session. */
export function readAuthTokens(raw: unknown): AuthTokens | null {
  const parsed = RawTokens.safeParse(raw);
  return parsed.success ? parsed.data : null;
}
